import {
  VITE_API_URL,
  VITE_ENV_NAME,
  VITE_IS_PUBLIC_SITE,
  VITE_PRODUCT_NAME,
} from '@/config';

export const isRS = () => {
  return VITE_PRODUCT_NAME === 'rs';
};

export const isPS = () => {
  return VITE_IS_PUBLIC_SITE === 'true';
};

export const isProd = () => {
  return ['prod', 'preprod'].includes(VITE_ENV_NAME);
};

/**
 * 原則、{@link isProd} を使うこと。preprod と prod のロジック的な差分がないようにするため。
 */
export const isProdOnly = () => {
  return VITE_ENV_NAME === 'prod';
};

export const isOnlyLocalhost = () => {
  return (
    location.hostname === 'localhost' && VITE_API_URL.includes('localhost')
  );
};
export const isDevMode = () => {
  return (
    isOnlyLocalhost() || VITE_ENV_NAME === 'dev' || VITE_ENV_NAME === 'local'
  );
};

const isGNetDomain = () => {
  return location.hostname.includes('rs.hq.admix.go.jp');
};

// via G-Net for RS
const rsGNetDomains = {
  prod: 'rs.hq.admix.go.jp',
  stg: 'stg.rs.hq.admix.go.jp',
  dev: 'dev.rs.hq.admix.go.jp',
} as const;

// via Internet for RS
const rsInternetDomains = {
  prod: 'app.rssystem.go.jp',
  stg: 'app.stg.rssystem.go.jp',
  dev: 'app.dev.rssystem.go.jp',
};

const rsAdminDomains = {
  prod: isGNetDomain() ? rsGNetDomains.prod : rsInternetDomains.prod,
  preprod: 'preprod.rs.wise-vine.com', // preprod は G-Net 経由のドメインがないため、インターネット環境のURLを指定
  stg: isGNetDomain() ? rsGNetDomains.stg : rsInternetDomains.stg,
  dev: isGNetDomain() ? rsGNetDomains.dev : rsInternetDomains.dev,
};

const publicSiteBaseUrls = {
  prod: 'rssystem.go.jp',
  preprod: 'pub.preprod.rs.wise-vine.com',
  stg: isGNetDomain()
    ? 'stg.rs.hq.admix.go.jp/public-site'
    : 'stg.rssystem.go.jp',
  dev: isGNetDomain()
    ? 'dev.rs.wise-vine.com/public-site'
    : 'pub.dev.rs.wise-vine.com',
};

const proxyRequiredDomains = [
  ...Object.values(rsGNetDomains),
  ...Object.values(rsInternetDomains),
  // rssystem.go.jp とかの公開サイトは、プロキシ不要
];

export const isProxyRequired = () => {
  return (
    // プロキシが必要なドメインの場合
    proxyRequiredDomains.some((domain) => location.hostname === domain) ||
    // もしくは、localhost の場合
    isOnlyLocalhost()
  );
};

// rsGNetDomains の場合、ネットワーク経路的に、Sendgrid が使えないため、パスワードリセットのボタンを表示しない
// そもそも、rsGNetDomains は、基本 GIMA による SSO 認証をしているため、パスワードリセットの必要性がないはず？
// なんなら、そもそもEメールアドレス・パスワードの入力項目も必要ないかもしれない。
export const isPasswordResettable = () => {
  return !Object.values(rsGNetDomains).some(
    (domain) => location.hostname === domain,
  );
};

/**
 * 環境に応じたドメインを取得
 */
export const getDomainService = ({ isPS }: { isPS: boolean }): string => {
  // 環境に応じたG-Netドメインを取得
  const domain = (() => {
    switch (VITE_ENV_NAME) {
      case 'prod':
        return !isPS ? rsAdminDomains.prod : publicSiteBaseUrls.prod;
      case 'preprod':
        return !isPS ? rsAdminDomains.preprod : publicSiteBaseUrls.preprod;
      case 'stg':
        return !isPS ? rsAdminDomains.stg : publicSiteBaseUrls.stg;
      case 'dev':
        // rsGNetDomains.devは現状使用しておらず、WVメンバーしか使用しないため。インターネット環境のURL指定
        return !isPS ? 'dev.rs.wise-vine.com' : publicSiteBaseUrls.dev;
      default:
        // local/mock等の場合はlocalhostを使用
        return location.host;
    }
  })();

  const protocol =
    VITE_ENV_NAME === 'local' || VITE_ENV_NAME === 'mock'
      ? location.protocol
      : 'http:';
  return `${protocol}//${domain}`;
};
